<template>
  <div id="prompting" class="mb-24">
    <div class="flex justify-center py-10">
      <div
        class="relative py-5 overflow-hidden border w-180 h-min bg-pure-white border-slate-400 rounded-3xl"
      >
        <div
          id="prompting-header"
          class="flex items-center justify-between gap-10 py-5 border-b bg-pure-white border-grey-200"
        >
          <div class="flex flex-col gap-1 px-10 ">
            <div class="flex items-center gap-2">
              <BlTextTitle3 text="Prompts Setting" />
              <BlBubbleCount :count="centerPrompts.length || 0" />
            </div>
            <div
              class="text-grey-400"
              :style="
                currentUser.role.includes('admin') ? { width: '70ch' } : {}
              "
            >
              <BlTextBody2
                text="These prompts are used in Skill-Based Treatment. Changing or deleting them here"
              />
              <BlTextBody2 text="will not" class="font-bold" />
              <BlTextBody2
                text="affect the existing prompts already used by the client."
              />
            </div>
          </div>
        </div>
        <div v-if="pageLoading" class="grid h-128 place-items-center">
          <div class="blum-loader"></div>
        </div>
        <div v-else id="prompting-body" class="flex flex-col items-center">
          <div v-if="!currentUser.role.includes('admin')" class="w-full px-6">
            <div
              class="flex items-center gap-2 px-4 py-2 rounded-b bg-info-100"
            >
              <div
                class="grid flex-shrink-0 w-10 h-10 place-items-center"
                :style="{ background: '#E4F1FA', borderRadius: '0.25rem' }"
              >
                <BlIcon icon="toast-info" class="w-6 h-6" />
              </div>
              <BlTextBody1
                class="text-info"
                text="Please note that the ability to add or edit in the Repository is restricted to users with the Admin role."
              />
            </div>
          </div>

          <div class="flex flex-col w-full gap-6 py-6">
            <div>
              <div class="px-10">
                <div
                  class="flex items-center justify-center py-4 border rounded-lg bg-slate-100 border-grey-200"
                >
                  <PromptPreview
                    :prompts="sortedPrompts"
                    :style="{ transform: 'scale(0.75 , 0.75)' }"
                  />
                </div>
              </div>
            </div>
            <div class="px-10 overflow-x-hidden">
              <div class="flex items-start justify-start w-full gap-4 mb-2">
                <div
                  v-if="currentUser.role.includes('admin')"
                  class="flex-shrink-0 w-2"
                />
                <BlTextSubtitle4
                  text="Score (%)"
                  class="flex-shrink-0 w-15"
                  :style="{ color: '#667085' }"
                />
                <BlTextSubtitle4
                  text="Code"
                  class="flex-shrink-0 w-15"
                  :style="{ color: '#667085' }"
                />
                <BlTextSubtitle4
                  text="Prompt name"
                  class="w-full"
                  :style="{ color: '#667085' }"
                />
                <div
                  v-if="currentUser.role.includes('admin')"
                  class="flex-shrink-0 w-6"
                />
              </div>
              <div
                :class="currentUser.role.includes('admin') ? 'space-y-4' : ''"
              >
                <div
                  v-for="prompt in sortedPrompts"
                  :key="prompt.id"
                  :id="prompt.id"
                  class="flex items-center gap-4"
                >
                  <PromptItem
                    :prompt="prompt"
                    @update="onUpdate"
                    @delete="onRemove"
                    :readOnly="!currentUser.role.includes('admin')"
                  />
                </div>
              </div>
            </div>
            <div v-if="currentUser.role.includes('admin')" class="px-10 mt-2">
              <button
                class="flex items-center gap-2 outline-none"
                style="width: fit-content;"
                @click="onCreate"
              >
                <BlIcon icon="plus" class="w-6 h-6" />
                <span class="font-bold text-primary">Add prompt</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import PromptItem from "./PromptingItem.vue";
import PromptPreview from "./PromptingPreview.vue";

import BlBubbleCount from "../../BlBubbleCount.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextTitle3 from "../../BlTextTitle3.vue";
import BlTextSubtitle4 from "../../BlTextSubtitle4.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlIcon from "../../BlIcon.vue";

export default {
  name: "PromptingSettings",
  components: {
    PromptItem,
    PromptPreview,
    BlTextTitle3,
    BlBubbleCount,
    BlTextBody2,
    BlInputButton,
    BlTextSubtitle4,
    BlTextBody1,
    BlIcon
  },
  data() {
    return {
      pageLoading: true,
      promptPageIndex: 0
    };
  },
  computed: {
    ...mapGetters(["currentUser", "centerPrompts"]),
    promptPageLimit() {
      return 9;
    },
    promptPageCount() {
      const totalBoxes = this.centerPrompts.length;
      return Math.ceil(totalBoxes / this.promptPageLimit);
    },
    sortedPrompts() {
      return this.sortPrompts(this.centerPrompts);
    }
  },
  async mounted() {
    this.promptPageIndex = 0;
    const params = {
      params: `?type=Prompt::Sbt`
    };
    this.pageLoading = true;
    await this.getCenterPrompts(params);
    this.pageLoading = false;
  },
  methods: {
    ...mapActions([
      "getCenterPrompts",
      "createCenterPrompt",
      "updateCenterPrompt",
      "deleteCenterPrompt"
    ]),
    promptGetBoxes() {
      const start = this.promptPageIndex * this.promptPageLimit;
      const end = (this.promptPageIndex + 1) * this.promptPageLimit;
      return this.centerPrompts.slice(start, end);
    },
    onCreate() {
      const prompt = {
        name: "Type prompt",
        abbreviation: "XX",
        score: 0,
        color: "lake",
        type: "Prompt::Sbt"
      };
      this.createCenterPrompt({ prompt });
    },
    onUpdate(data) {
      const params = {
        id: data.id,
        prompt: { ...data, color: "lake" }
      };
      this.updateCenterPrompt(params);
    },
    onRemove(data) {
      const params = {
        id: data.id,
        prompt: data
      };
      this.deleteCenterPrompt(params);
    },
    sortPrompts(prompts) {
      return prompts.sort((a, b) => {
        if (a.is_default && a.name === "Independent") return -1;
        if (b.is_default && b.name === "Independent") return 1;

        if (a.is_default && a.name === "Incorrect") return 1;
        if (b.is_default && b.name === "Incorrect") return -1;

        return b.score - a.score;
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/repository/sbt/Prompting.vue